import React, { useState } from 'react';
import PropTypes from 'prop-types';

// Utils
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import useImage from '../../../../utils/hooks/useImage';
import useModal from '../../../../utils/hooks/useModal';
import cn from '../../../../utils/tools/cn';
import PermissionsGate from '../../../../utils/gate/permissionsGate';
import SCOPE from '../../../../utils/gate/scope';

// Icons
import { ReactComponent as SearchIcon } from '../../../../assets/icons/search.svg';

// Components
import Button from '../../../atoms/Button/Button';
import InputField from '../../../atoms/InputField/InputField';
import FlashMessage from '../../FlashMessage/FlashMessage';
import AddNewMedia from '../../../views/common/MediaLibrary/AddNewMedia/AddNewMedia';

import styles from './MediaGrid.module.scss';

function MediaGrid({
  mediasFromLibrary, handleValidateSelection, max, min, preSelection, refetchMedias,
}) {
  const { t } = useTranslation();
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const { getImageSrc } = useImage();
  const { closeModal } = useModal();

  const [uploadErrors, setUploadErrors] = useState([]);
  const [uploadSuccess, setUploadSuccess] = useState([]);

  const [selectedMedias, setSelectedMedias] = useState(
    mediasFromLibrary?.filter((m) => preSelection?.some((media) => m.id === media.id)) || [],
  );

  const [searchFilter, setSearchFilter] = useState('');

  const handleOrRemoveFromSelection = (media) => {
    if (selectedMedias.some((m) => m.fileId === media.fileId)) {
      // remove
      setSelectedMedias((state) => state.filter((m) => m.fileId !== media.fileId));
    } else {
      // add
      if (!max) {
        setSelectedMedias((state) => state.concat([media]));
        return;
      }

      if (max && max === 1) {
        setSelectedMedias([media]);
        return;
      }

      if (max && max !== 1 && selectedMedias.length < max) {
        setSelectedMedias((state) => state.concat([media]));
      }
    }
  };

  return (
    <div>
      <div className="d-flex j-space-btw p-b-20 mobile-d-col mobile-a-stretch">
        <InputField
          value={searchFilter}
          onChange={(input) => setSearchFilter(input.target.value)}
          label={t('medias.input.search')}
          className={styles.search}
          icon={<SearchIcon />}
        />

        <PermissionsGate scopes={[SCOPE.create_pla_media, SCOPE.create_ownEst_media]}>
          <div className="mobile-m-t-20">
            <AddNewMedia
              setUploadErrors={setUploadErrors}
              setUploadSuccess={setUploadSuccess}
              buttonLabel={t('medias.input.addNew')}
              buttonTheme="inverted"
              handleAfterAdding={refetchMedias}
            />
          </div>
        </PermissionsGate>
      </div>

      {uploadErrors?.length ? (
        <div className="d-flex d-col m-b-10">
          {uploadErrors.map((error, index) => (
            <FlashMessage className="m-b-5" key={index} message={error} color="error" />
          ))}
        </div>
      ) : null}

      {uploadSuccess?.length ? (
        <div className="d-flex d-col m-b-10">
          {uploadSuccess.map((error, index) => (
            <FlashMessage className="m-b-5" key={index} message={error} color="success" />
          ))}
        </div>
      ) : null}

      <div className="d-flex f-wrap p-t-20">
        {mediasFromLibrary.filter((media) => media.name.includes(searchFilter))?.length ? (
          mediasFromLibrary.filter((media) => media.name.includes(searchFilter)).map((media) => (
            <div className={styles.image_holder}>
              <button
                type="button"
                onClick={() => handleOrRemoveFromSelection(media)}
                className={cn([
                  styles.image_selector,
                  selectedMedias?.some((m) => m.fileId === media.fileId) ? styles.selected : '',
                ])}
              >
                <img
                  alt={media.name}
                  src={getImageSrc({
                    fileId: media.fileId,
                    widths: {
                      default: 325,
                      mobile: 145,
                      tablet: 440,
                      smallLaptop: 345,
                    },
                  })}
                />
              </button>

              <p className={styles.image_name} title={media.name}>
                {media.name}
              </p>
            </div>
          ))
        ) : (
          <div className="d-flex m-b-40">
            <FlashMessage
              message={searchFilter ? t('medias.input.noResult') : t('medias.input.noImage')}
            />
          </div>
        )}
      </div>

      <div
        className="sticky-actions sticky-actions--modal bg-white d-flex a-center j-space-btw m-b-20 mobile-a-stretch"
      >
        {!isMobile ? (
          <p className="m-r-20 c-primary-500">
            {selectedMedias?.length ? (
              t(
                selectedMedias.length > 1
                  ? 'medias.input.selectedElements'
                  : 'medias.input.selectedElement',
                { count: selectedMedias.length },
              )
            ) : ''}
          </p>
        ) : null}

        <div className="d-flex a-center j-end mobile-d-col a-stretch">
          <Button
            theme="inverted"
            className="m-r-10 mobile-m-r-0 mobile-m-b-10"
            onClick={() => closeModal()}
            full={isMobile}
          >
            {t('common.cancel')}
          </Button>
          <Button
            disabled={!selectedMedias?.length || selectedMedias.length < min}
            onClick={() => {
              handleValidateSelection(selectedMedias);
              closeModal();
            }}
            full={isMobile}
          >
            {selectedMedias.length <= 1
              ? t('medias.input.selectThisImage')
              : t('medias.input.selectMultiImages', { count: selectedMedias.length })}
          </Button>
        </div>
      </div>
    </div>
  );
}

MediaGrid.propTypes = {
  handleValidateSelection: PropTypes.func,
  mediasFromLibrary: PropTypes.arrayOf(PropTypes.shape({})),
  max: PropTypes.number,
  min: PropTypes.number,
  preSelection: PropTypes.arrayOf(PropTypes.shape({})),
  refetchMedias: PropTypes.func,
};

MediaGrid.defaultProps = {
  handleValidateSelection: () => {},
  mediasFromLibrary: [],
  max: null,
  min: null,
  preSelection: [],
  refetchMedias: () => {},
};

export default MediaGrid;
